import api from './client';
import type {
  Connection,
  ConnectionListResponse,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  ConnectionTestResult,
  IngestionStatusResponse,
  OAuthInitiateResponse,
  OAuthCallbackResponse,
  AdminConsentInitiateResponse,
  EntityListResponse,
  SelectedEntity,
  SaveEntitiesBody,
  DiscoverEntitiesResponse,
  EntityDiscoveryStatusResponse,
  EntitySchemaInspection,
  EntityPreviewInspection,
} from './types';

export const connectionsService = {
  /**
   * Get user connections with pagination
   */
  getUserConnections: async (
    page: number = 1,
    limit: number = 20,
    connectorId?: string
  ): Promise<ConnectionListResponse> => {
    let endpoint = `/connections?page=${page}&limit=${limit}`;
    if (connectorId) {
      endpoint += `&connectorId=${encodeURIComponent(connectorId)}`;
    }

    return api.get<ConnectionListResponse>(endpoint);
  },

  /**
   * Create a new connection
   */
  createConnection: async (data: CreateConnectionRequest): Promise<Connection> => {
    return api.post<Connection>('/connections', data);
  },

  /**
   * Get connection by ID
   */
  getConnection: async (id: string): Promise<Connection> => {
    return api.get<Connection>(`/connections/${id}`);
  },

  /**
   * Update connection
   */
  updateConnection: async (
    id: string,
    data: UpdateConnectionRequest
  ): Promise<Connection> => {
    // Candidate inspection has a 120-second upstream deadline; allow time for persistence.
    return api.put<Connection>(`/connections/${id}`, data, { timeout: 150000 });
  },

  /**
   * Delete connection
   */
  deleteConnection: async (id: string): Promise<void> => {
    return api.delete<void>(`/connections/${id}`);
  },

  /**
   * Test connection
   */
  testConnection: async (id: string): Promise<ConnectionTestResult> => {
    return api.post<ConnectionTestResult>(`/connections/${id}/test`);
  },

  /**
   * Get ingestion status for a connection (poll for progress)
   */
  getIngestionStatus: async (id: string): Promise<IngestionStatusResponse> => {
    return api.get<IngestionStatusResponse>(`/connections/${id}/ingestion-status`);
  },

  /**
   * Cancel an in-progress ingestion for a connection
   */
  cancelIngestion: async (id: string): Promise<{ runId: string; cancelled: boolean }> => {
    return api.post<{ runId: string; cancelled: boolean }>(`/connections/${id}/cancel-ingestion`);
  },

  /**
   * Trigger a manual re-sync for a connection.
   * For incremental connections, only new/changed data since the last sync is fetched.
   */
  triggerSync: async (id: string): Promise<{ runId: string; connectionId: string }> => {
    return api.post<{ runId: string; connectionId: string }>(`/connections/${id}/sync`);
  },

  // ─── OAuth ───────────────────────────────────────────────────────────────

  /**
   * Initiate OAuth flow for a connection.
   * Returns the authorization URL to redirect the user to.
   */
  initiateOAuth: async (id: string): Promise<OAuthInitiateResponse> => {
    return api.post<OAuthInitiateResponse>(`/connections/${id}/auth/oauth/initiate`);
  },

  initiateAdminConsent: async (tenantId: string, state: string): Promise<AdminConsentInitiateResponse> => {
    return api.post<AdminConsentInitiateResponse>('/connections/application-insights/admin-consent', { tenantId, state });
  },

  getAdminConsentStatus: async (tenantId: string): Promise<{
    granted: boolean;
    message: string;
    client_id: string;
    application_name: string;
  }> => {
    return api.post('/connections/application-insights/admin-consent/status', { tenantId });
  },

  verifyApplicationInsightsAccess: async (config: {
    tenantId: string;
    subscriptionId: string;
    resourceGroupName: string;
    applicationInsightsName: string;
    lookbackDays: number;
  }): Promise<ConnectionTestResult> => {
    return api.post<ConnectionTestResult>('/connections/application-insights/verify', config, { suppressErrorCapture: true });
  },

  /**
   * Handle OAuth callback (called from the OAuth callback page).
   */
  handleOAuthCallback: async (
    connector: string,
    params: URLSearchParams,
  ): Promise<OAuthCallbackResponse> => {
    const callbackParams = connector === 'application-insights'
      ? new URLSearchParams({ state: params.get('state') ?? '' })
      : params;
    const endpoint = `/connections/oauth/callback/${encodeURIComponent(connector)}?${callbackParams.toString()}`;
    return api.get<OAuthCallbackResponse>(endpoint, {
      skipAuth: connector === 'application-insights',
    });
  },

  // ─── Entity Discovery & Selection ────────────────────────────────────────

  /**
   * List available entities for a connection (synchronous fallback).
   */
  listEntities: async (id: string): Promise<EntityListResponse> => {
    return api.get<EntityListResponse>(`/connections/${id}/entities`);
  },

  /**
   * Start async entity discovery for a connection.
   * Returns immediately with status 'connecting'.
   * Poll getDiscoveryStatus() for progress.
   *
   * @param forceRefresh  If true, skip all caches and call the live connector API.
   */
  discoverEntities: async (id: string, forceRefresh?: boolean): Promise<DiscoverEntitiesResponse> => {
    return api.post<DiscoverEntitiesResponse>(
      `/connections/${id}/entities/discover`,
      forceRefresh ? { forceRefresh: true } : undefined,
    );
  },

  /**
   * Get current entity discovery status.
   * When status is 'complete', result contains the full entity list.
   */
  getDiscoveryStatus: async (id: string): Promise<EntityDiscoveryStatusResponse> => {
    return api.get<EntityDiscoveryStatusResponse>(`/connections/${id}/entities/status`);
  },

  /**
   * Get the current user's selected entities for a connection.
   * Entity selections are per-user (stored in user_connection_configs table).
   */
  getSelectedEntities: async (id: string): Promise<{ entities: SelectedEntity[] }> => {
    return api.get<{ entities: SelectedEntity[] }>(`/connections/${id}/entities/selected`);
  },

  inspectEntitySchema: async (id: string, entityName: string): Promise<EntitySchemaInspection> => {
    return api.get<EntitySchemaInspection>(`/connections/${id}/entities/${encodeURIComponent(entityName)}/schema`);
  },

  previewEntity: async (id: string, entityName: string, limit: number = 10): Promise<EntityPreviewInspection> => {
    return api.get<EntityPreviewInspection>(
      `/connections/${id}/entities/${encodeURIComponent(entityName)}/preview?limit=${limit}`,
    );
  },

  /**
   * Save entity selection for a connection.
   * Persists to per-user config (not connection metadata).
   */
  saveEntities: async (id: string, entities: SelectedEntity[], cdc?: SaveEntitiesBody['cdc']): Promise<Connection> => {
    return api.put<Connection>(`/connections/${id}/entities`, { entities, ...(cdc ? { cdc } : {}) });
  },
};

export default connectionsService;
